import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { useCreatives } from '@/hooks/useCreatives';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useDismissOnOutsideClick } from '@/hooks/useDismissOnOutsideClick';
import {
  DROPDOWN_ITEM_COMPACT,
  DROPDOWN_PANEL,
} from '@/components/primitives/dropdownMenuClasses';
import { deleteCreative } from '@/services/creatives';
import { formatRelativeDate } from '@/utils/date';
import type { Creative, Campaign } from '@/types/api';
import { creativeEditorLinkState } from '@/rve/editorBackNav';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';

type CreativeStatus = 'Ready' | 'Rendering' | 'Draft';

function inferStatus(c: Creative): CreativeStatus {
  // No render status in API — infer from thumbnail
  if (c.thumbnail_url) return 'Ready';
  return 'Draft';
}

const STATUS_CONFIG: Record<CreativeStatus, { classes: string; icon?: string }> = {
  Ready: {
    classes: 'aspectly:bg-white aspectly:text-tertiary aspectly:border aspectly:border-outline-variant/40',
  },
  Rendering: {
    classes: 'aspectly:bg-primary-container aspectly:text-primary aspectly:border aspectly:border-primary-fixed',
    icon: 'autorenew',
  },
  Draft: {
    classes: 'aspectly:bg-surface-container-high aspectly:text-on-surface-variant',
  },
};

function CreativeThumb({ creative }: { creative: Creative }) {
  if (creative.thumbnail_url) {
    return (
      <img
        src={creative.thumbnail_url}
        alt={creative.name}
        className="aspectly:h-14 aspectly:w-20 aspectly:shrink-0 aspectly:rounded-lg aspectly:object-cover aspectly:shadow-xs"
      />
    );
  }
  const status = inferStatus(creative);
  const colors: Record<CreativeStatus, string> = {
    Ready: 'aspectly:from-primary-container aspectly:to-surface-container-low',
    Rendering: 'aspectly:from-primary-fixed aspectly:to-primary-container',
    Draft: 'aspectly:from-surface-container aspectly:to-surface-container-high',
  };
  return (
    <div
      className={`aspectly:h-14 aspectly:w-20 aspectly:shrink-0 aspectly:rounded-lg aspectly:shadow-xs aspectly:bg-linear-to-br ${colors[status]} aspectly:flex aspectly:items-center aspectly:justify-center`}
    >
      <span className="material-symbols-outlined aspectly:text-primary/30 aspectly:text-2xl">image</span>
    </div>
  );
}

function groupByCampaign(
  creatives: Creative[],
  campaigns: Campaign[],
): Array<{ campaignName: string; items: Creative[] }> {
  if (creatives.length === 0) return [];

  // Build a map: campaign_id → name
  const campaignMap = new Map<number, string>(campaigns.map((c) => [c.id as number, c.name]));

  // Group creative by first assigned campaign
  const groups = new Map<string, Creative[]>();
  for (const cr of creatives) {
    const firstCampaignId = cr.assigned_campaign_ids?.[0];
    const campaignName =
      firstCampaignId != null
        ? (campaignMap.get(firstCampaignId) ?? `Campaign #${firstCampaignId}`)
        : 'Unassigned';
    const existing = groups.get(campaignName) ?? [];
    existing.push(cr);
    groups.set(campaignName, existing);
  }

  return Array.from(groups.entries()).map(([campaignName, items]) => ({
    campaignName,
    items,
  }));
}

const SORT_OPTIONS = ['Latest', 'Oldest', 'Name A–Z'];

export default function CreativesPage() {
  const orgId = useOrgId();
  const [sortBy, setSortBy] = useState('Latest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const closeSortMenu = useCallback(() => setShowSortMenu(false), []);

  useDismissOnOutsideClick({
    isActive: showSortMenu,
    onDismiss: closeSortMenu,
    containerRef: sortMenuRef,
  });
  const { data: creatives, loading, error, refetch } = useCreatives();
  const { data: campaigns } = useCampaigns();

  const sorted = [...(creatives ?? [])].sort((a, b) => {
    if (sortBy === 'Latest')
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    if (sortBy === 'Oldest')
      return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
    return a.name.localeCompare(b.name);
  });

  const groups = groupByCampaign(sorted, campaigns ?? []);

  async function handleDelete(e: React.MouseEvent, id: number) {
    e.preventDefault();
    if (!confirm('Delete this creative?')) return;
    try {
      await deleteCreative(id);
      refetch();
    } catch {
      // silently refetch to get latest state
      refetch();
    }
  }

  return (
    <div className="aspectly:p-4 aspectly:md:p-8 aspectly:lg:p-12 aspectly:flex-1 aspectly:space-y-10">
      {/* Header */}
      <div className="aspectly:flex aspectly:flex-wrap aspectly:items-center aspectly:justify-between aspectly:gap-4 aspectly:pb-4 aspectly:border-b aspectly:border-surface-container-highest">
        <h1 className="aspectly:text-4xl aspectly:font-headline aspectly:font-black aspectly:text-on-surface aspectly:tracking-tight">
          Creatives
        </h1>
        <div className="aspectly:flex aspectly:items-center aspectly:gap-3">
          {/* Sort dropdown */}
          <div ref={sortMenuRef} className="aspectly:relative">
            <button
              type="button"
              onClick={() => setShowSortMenu((v) => !v)}
              className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-2 aspectly:rounded-xl aspectly:h-10 aspectly:px-4 aspectly:bg-surface-container-low aspectly:text-on-surface aspectly:text-sm aspectly:font-medium aspectly:hover:bg-surface-variant aspectly:transition-colors aspectly:border aspectly:border-surface-variant aspectly:cursor-pointer"
              aria-expanded={showSortMenu}
            >
              <span>Sort by: {sortBy}</span>
              <span className="material-symbols-outlined aspectly:text-[18px]">expand_more</span>
            </button>
            {showSortMenu && (
              <div className={`${DROPDOWN_PANEL} aspectly:right-0 aspectly:py-1 aspectly:w-44`}>
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setSortBy(opt);
                      closeSortMenu();
                    }}
                    className={`${DROPDOWN_ITEM_COMPACT} ${
                      sortBy === opt
                        ? 'aspectly:text-primary aspectly:font-bold aspectly:bg-primary-container' : 'aspectly:text-on-surface aspectly:hover:bg-surface-container-low'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsNewCreativeOpen(true)}
            className="aspectly:flex aspectly:items-center aspectly:gap-2 aspectly:bg-primary aspectly:hover:bg-primary-dim aspectly:text-on-primary aspectly:px-4 aspectly:py-2.5 aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:transition-all aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:cursor-pointer"
          >
            <span className="material-symbols-outlined aspectly:text-lg">add</span>
            Creative
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
          {error}
        </div>
      )}

      {/* Skeletons */}
      {loading && (
        <div className="aspectly:space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="aspectly:h-20 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10 aspectly:animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Campaign groups */}
      {!loading &&
        groups.map(({ campaignName, items }) => (
          <section key={campaignName} className="aspectly:space-y-4">
            <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:px-2">
              <h3 className="aspectly:text-lg aspectly:font-headline aspectly:font-bold aspectly:text-on-surface aspectly:tracking-tight">
                {campaignName === 'Unassigned' ? 'Unassigned' : `Campaign: ${campaignName}`}
              </h3>
              <span className="aspectly:px-2.5 aspectly:py-0.5 aspectly:rounded-full aspectly:bg-secondary-container aspectly:text-on-secondary-container aspectly:text-xs aspectly:font-medium">
                {items.length} {items.length === 1 ? 'Item' : 'Items'}
              </span>
            </div>

            <div className="aspectly:flex aspectly:flex-col aspectly:gap-3">
              {items.map((cr) => {
                const status = inferStatus(cr);
                const cfg = STATUS_CONFIG[status];
                return (
                  <Link
                    key={cr.id}
                    to={orgUiPath(orgId, `/creatives/${cr.id}`)}
                    state={creativeEditorLinkState('creatives')}
                    className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:p-4 aspectly:rounded-xl aspectly:bg-surface-container-lowest aspectly:ambient-shadow aspectly:transition-transform aspectly:hover:-translate-y-0.5 aspectly:border aspectly:border-surface-variant/20 aspectly:group"
                  >
                    <div className="aspectly:flex aspectly:items-center aspectly:gap-6">
                      <CreativeThumb creative={cr} />
                      <div className="aspectly:flex aspectly:flex-col aspectly:justify-center">
                        <p className="aspectly:text-on-surface aspectly:text-base aspectly:font-semibold aspectly:leading-tight aspectly:group-hover:text-primary aspectly:transition-colors">
                          {cr.name}
                        </p>
                        <p className="aspectly:text-on-surface-variant aspectly:text-sm aspectly:mt-1">
                          Last Edited: {formatRelativeDate(cr.updated_at)}
                        </p>
                      </div>
                    </div>

                    <div className="aspectly:flex aspectly:items-center aspectly:gap-4">
                      <div
                        className={`aspectly:px-3 aspectly:py-1 aspectly:rounded-md aspectly:text-[10px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:flex aspectly:items-center aspectly:gap-1.5 ${cfg.classes}`}
                      >
                        {cfg.icon && (
                          <span
                            className={`material-symbols-outlined aspectly:text-[14px] ${status === 'Rendering' ? 'aspectly:animate-spin' : ''}`}
                          >
                            {cfg.icon}
                          </span>
                        )}
                        {status === 'Ready' && (
                          <span className="aspectly:w-1.5 aspectly:h-1.5 aspectly:rounded-full aspectly:bg-tertiary" />
                        )}
                        {status}
                      </div>

                      <button
                        aria-label="Download"
                        disabled={status !== 'Ready'}
                        onClick={(e) => e.preventDefault()}
                        className={`aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-lg aspectly:size-9 aspectly:transition-colors ${
                          status === 'Ready'
                            ? 'aspectly:bg-surface-container-high aspectly:text-on-surface aspectly:hover:bg-primary-container aspectly:hover:text-primary aspectly:cursor-pointer' : 'aspectly:text-on-surface-variant aspectly:opacity-50 aspectly:cursor-not-allowed'
                        }`}
                      >
                        <span className="material-symbols-outlined aspectly:text-[20px]">download</span>
                      </button>

                      <button
                        aria-label="Delete creative"
                        onClick={(e) => cr.id != null && handleDelete(e, cr.id)}
                        className="aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-lg aspectly:size-9 aspectly:text-on-surface-variant aspectly:hover:bg-error-container aspectly:hover:text-error aspectly:transition-colors aspectly:cursor-pointer"
                      >
                        <span className="material-symbols-outlined aspectly:text-[20px]">delete</span>
                      </button>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

      {/* Empty state */}
      {!loading && groups.length === 0 && !error && (
        <div className="aspectly:py-24 aspectly:text-center">
          <span className="material-symbols-outlined aspectly:text-6xl aspectly:text-primary/30">auto_awesome</span>
          <p className="aspectly:mt-4 aspectly:text-on-surface-variant aspectly:font-medium">No creatives yet.</p>
          <button
            type="button"
            onClick={() => setIsNewCreativeOpen(true)}
            className="aspectly:mt-4 aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:bg-primary aspectly:text-on-primary aspectly:px-6 aspectly:py-3 aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:hover:bg-primary-dim aspectly:transition-all aspectly:cursor-pointer"
          >
            <span className="material-symbols-outlined aspectly:text-lg">add</span>
            Start your first creative
          </button>
        </div>
      )}

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        onCreated={(created) =>
          navigate(orgUiPath(orgId, `/creatives/${created.id}`), {
            state: creativeEditorLinkState('creatives'),
          })
        }
      />
    </div>
  );
}
